import { useEffect, useMemo, useRef, useState } from "react";
import DeptOptions from "./DeptOptions";
import api from "../api/client";
import { Modal, MicButton, Avatar, TabButton, TabTrack } from "./ui";
import Icon from "./Icon";
import PersonPicker from "./PersonPicker";
import FloatingPopover from "./FloatingPopover";
import { useToast } from "../store/toast";
import { useAuth } from "../store/auth";
import { useMeta } from "../hooks";
import { STATUS, PRIORITY, TASK_TYPES, SUPPLY, SUPPLY_ORDER } from "../constants";
import { fmtStamp, todayLocal, todayPlusLocal } from "../utils";
import CustomFieldsForm from "./CustomFieldsForm";
import { HorasMin } from "./TaskDetailPanel";

const EMPTY = {
  title: "", description: "", priority: "medium", status: "not_started",
  progress_pct: 0, start_date: "", due_date: "", start_at: "", end_at: "", department_id: "",
  project_id: "", phase_id: "", blocker_reason: "", support_required: "", closure_note: "",
  assignee_ids: [], collaborator_ids: [], pmo_ids: [], tags: [],
  task_type: "operativa", recurrence: "", hours_spent: 0, items: [], dependency_ids: [], custom_data: {},
  pending_files: [],  // archivos a subir DESPUÉS de guardar (solo estado local del modal)
};

// Nota: las claves internas (inventario/solicitud) NO cambian —conservan su
// lógica (lista de ítems, packing, etc.)—; solo se renombra su etiqueta visible.
// TASK_TYPES vive en constants.js (fuente única, compartida con la config de
// pestañas personalizadas).
const RECURRENCE = [["", "No repite"], ["daily", "Diaria"], ["weekly", "Semanal"], ["biweekly", "Quincenal"], ["monthly", "Mensual"]];

function Chip({ selected, onClick, children, color }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-tag border px-2 py-0.5 text-[11px] font-medium transition-all ${selected ? `${color || "border-brand-400 bg-brand-50 text-brand-700"} ring-1 ring-brand-300` : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function UserChip({ user, selected, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-tag border px-2 py-0.5 text-[11px] font-medium transition-all ${selected ? "border-brand-400 bg-brand-50 text-brand-700 ring-1 ring-brand-300" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50"}`}>
      <Avatar name={user.full_name} size={18} src={user.avatar_url ? `/api/users/${user.id}/avatar` : ""} />
      <span className="max-w-[120px] truncate">{user.full_name}</span>
      {selected && <span className="text-brand-500">&#10003;</span>}
    </button>
  );
}

function Divider({ label }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-gradient-to-r from-slate-200/80 to-transparent" />
      {label && <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-300">{label}</span>}
      <div className="h-px flex-1 bg-gradient-to-l from-slate-200/80 to-transparent" />
    </div>
  );
}

function DateField({ label, icon, value, onChange, type = "date", required }) {
  const hasValue = Boolean(value);
  return (
    <div className={`relative rounded-lg border p-2 transition-all ${hasValue ? "border-brand-200 bg-brand-50/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      <div className="mb-1 flex items-center gap-1.5">
        <Icon name={icon} size={13} className={hasValue ? "text-brand-500" : "text-slate-400"} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}{required && <span className="text-rose-500"> *</span>}</span>
      </div>
      <input type={type} aria-label={label} className="w-full border-0 bg-transparent p-0 text-[12px] font-medium text-slate-700 focus:outline-none focus:ring-0"
        value={value} onChange={onChange} />
    </div>
  );
}

export default function TaskModal({ open, onClose, onSaved, task, departments, users, projects = [], onProjectCreated, lockDept, presetProjectId, presetType, defaultAssignee }) {
  const { push } = useToast();
  const { user, readonly, features } = useAuth();
  const { misDepartamentos } = useMeta();
  // Departamentos elegibles: su depto + anidados (todos para directivo/CFO/admin);
  // en edición, incluye el actual si quedó fuera de alcance. Pedido 2026-10-06.
  const deptOpts = useMemo(() => {
    const base = misDepartamentos || [];
    const curId = task?.department_id;
    if (curId && !base.some((d) => String(d.id) === String(curId))) {
      const cur = (departments || []).find((d) => String(d.id) === String(curId));
      if (cur) return [...base, cur];
    }
    return base;
  }, [misDepartamentos, departments, task]);
  // Marcas apagadas en Configuración → Preferencias de la plataforma (empresa.usa_marca)
  const usaMarca = features?.usa_marca !== false;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [requirements, setRequirements] = useState({});
  const [newProject, setNewProject] = useState("");
  const [assignable, setAssignable] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [tpls, setTpls] = useState(null);   // plantillas de materiales (null = picker cerrado)
  const [tplQ, setTplQ] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [tab, setTab] = useState("general");
  const [allTasks, setAllTasks] = useState([]);
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [depQuery, setDepQuery] = useState("");
  const [depResults, setDepResults] = useState([]);
  const depAnchorRef = useRef(null);
  const [progress, setProgress] = useState([]);
  const [newProgress, setNewProgress] = useState({ hours: "", description: "", work_date: todayLocal() });
  const [editProgressId, setEditProgressId] = useState(null);
  const [progressBusy, setProgressBusy] = useState(false);
  const initialSnapshot = useRef("");   // form state when the modal opened (dirty check)
  const editing = Boolean(task?.id);
  // Fields whose change means "the user has unsaved work" worth confirming.
  const snapshotOf = (f) => JSON.stringify({
    title: f.title, description: f.description, priority: f.priority, status: f.status,
    start_date: f.start_date, due_date: f.due_date, task_type: f.task_type,
    assignee_ids: f.assignee_ids, collaborator_ids: f.collaborator_ids, pmo_ids: f.pmo_ids,
    project_id: f.project_id, items: f.items, tags: f.tags, closure_note: f.closure_note,
    blocker_reason: f.blocker_reason, support_required: f.support_required,
  });
  const isDirty = () => initialSnapshot.current && snapshotOf(form) !== initialSnapshot.current;
  const requestClose = () => {
    if (isDirty() && !window.confirm("¿Cerrar sin guardar? Se perderán los cambios no guardados.")) return;
    onClose();
  };
  const isProject = form.task_type === "proyecto";
  // Tipos con lista de materiales/contenido (despacho = envío de equipos,
  // generado desde Etiquetas de packing).
  const isInventory = ["inventario", "solicitud", "despacho"].includes(form.task_type);
  const isReunion = form.task_type === "reunion";
  const supportsProgress = !["inventario", "solicitud"].includes(form.task_type);
  const setStartDT = (v) => setForm((f) => ({ ...f, start_at: v, start_date: v ? v.slice(0, 10) : "" }));
  const setEndDT = (v) => setForm((f) => ({ ...f, end_at: v, due_date: v ? v.slice(0, 10) : "" }));

  const items = form.items || [];
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { category: "", name: "", quantity: 1, unit: "", precio: "", note: "", done: false }] }));
  const setItem = (i, k, v) => setForm((f) => { const it = [...(f.items || [])]; it[i] = { ...it[i], [k]: v }; return { ...f, items: it }; });
  const removeItem = (i) => setForm((f) => ({ ...f, items: (f.items || []).filter((_, idx) => idx !== i) }));
  const parsePaste = () => {
    const out = [];
    pasteText.split(/\r?\n/).forEach((line) => {
      if (!line.trim()) return;
      const cols = line.split("\t");
      const name = (cols[0] || "").trim();
      if (!name) return;
      let qty = null;
      for (let j = 1; j < cols.length; j++) {
        const n = parseFloat(String(cols[j]).replace(",", "."));
        if (!isNaN(n)) { qty = n; break; }
      }
      if (qty === null) return;
      out.push({ category: "", name, quantity: qty, unit: "", precio: "", note: "", done: false });
    });
    if (!out.length) return push("No se detectaron filas con cantidad.", "warning");
    setForm((f) => ({ ...f, items: [...(f.items || []), ...out] }));
    setPasteText(""); setShowPaste(false);
    push(`${out.length} item(s) agregados`, "success");
  };

  // --- Importar una plantilla de materiales en los ítems de la tarea ---
  const openTpls = () => {
    setTplQ("");
    // Trae globales + las de la división del departamento de la tarea.
    const params = form.department_id ? { department_id: form.department_id } : {};
    api.get("/material-templates", { params })
      .then((r) => setTpls(r.data || [])).catch(() => setTpls([]));
  };
  const importTpl = (t) => {
    const nuevos = (t.items || []).map((i) => ({
      category: i.category || "", name: i.name || "",
      quantity: i.quantity ?? 1, unit: i.unit || "",
      precio: i.precio ?? (i.precio_cents ? (i.precio_cents / 100).toFixed(2) : ""),
      estado: i.estado || "", nota: i.nota || i.note || "", note: i.note || i.nota || "",
      done: false,
    }));
    if (!nuevos.length) return push("La plantilla no tiene ítems", "warning");
    setForm((f) => ({ ...f, items: [...(f.items || []), ...nuevos] }));
    setTpls(null);
    push(`${nuevos.length} ítem(s) importados de «${t.name}»`, "success");
  };

  useEffect(() => {
    if (!open) return;
    api.get("/config/task-requirements").then((r) => setRequirements(r.data.required_fields || {})).catch(() => {});
    api.get("/users/assignable").then((r) => setAssignable(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setAllTasks(r.data || [])).catch(() => setAllTasks([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let initial;
    if (task?.id) {
      initial = {
        ...EMPTY, ...task,
        start_date: task.start_date || "", due_date: task.due_date || "",
        start_at: task.start_at ? task.start_at.slice(0, 16) : "", end_at: task.end_at ? task.end_at.slice(0, 16) : "",
        department_id: task.department_id || "", project_id: task.project_id || "",
        phase_id: task.phase_id || "",
        assignee_ids: task.assignees?.map((a) => a.id) || [],
        collaborator_ids: task.collaborators?.map((a) => a.id) || [],
        pmo_ids: task.pmo?.map((a) => a.id) || [],
        closure_note: task.closure_note || "",
        tags: task.tags?.map((t) => t.name) || [],
        dependency_ids: task.dependencies?.map((d) => d.depends_on_id) || [],
        custom_data: task.custom_data || {},
      };
    } else {
      initial = {
        ...EMPTY, department_id: lockDept || "",
        // Por defecto: inicio = hoy, compromiso = hoy + 7 días. Así ninguna
        // tarea queda sin fechas y hay una ventana de trabajo razonable.
        start_date: todayLocal(), due_date: todayPlusLocal(7),
        assignee_ids: defaultAssignee ? [defaultAssignee] : [],
        ...(presetProjectId ? { project_id: presetProjectId, task_type: "proyecto" } : {}),
        ...(presetType ? { task_type: presetType, ...(presetProjectId ? { project_id: presetProjectId } : {}) } : {}),
      };
    }
    setForm(initial);
    initialSnapshot.current = snapshotOf(initial);
    setNewProject(""); setShowPaste(false); setPasteText(""); setAssigneeFilter(""); setTab("general"); setDepQuery("");
    setProgress(task?.progress_entries || []);
    setNewProgress({ hours: "", description: "", work_date: todayLocal() });
    setEditProgressId(null);
  }, [open, task, lockDept, presetProjectId, presetType, defaultAssignee]);

  const reloadProgress = async () => {
    if (!task?.id) return;
    try {
      const r = await api.get(`/tasks/${task.id}/progress`);
      setProgress(r.data || []);
    } catch { /* ignore */ }
  };

  const saveProgress = async () => {
    if (!task?.id) return;
    if (!newProgress.description.trim()) return push("Describe el avance", "error");
    setProgressBusy(true);
    try {
      if (editProgressId) {
        await api.patch(`/tasks/${task.id}/progress/${editProgressId}`, { hours: 0, description: newProgress.description, work_date: newProgress.work_date || null });
      } else {
        await api.post(`/tasks/${task.id}/progress`, { hours: 0, description: newProgress.description, work_date: newProgress.work_date || null });
      }
      setNewProgress({ hours: "", description: "", work_date: todayLocal() });
      setEditProgressId(null);
      await reloadProgress();
    } catch (err) {
      push(err.response?.data?.detail || "Error al guardar avance", "error");
    } finally { setProgressBusy(false); }
  };

  const deleteProgressEntry = async (id) => {
    if (!task?.id) return;
    if (!window.confirm("¿Eliminar este avance? Las horas se descontarán del total.")) return;
    try {
      await api.delete(`/tasks/${task.id}/progress/${id}`);
      await reloadProgress();
    } catch (err) { push(err.response?.data?.detail || "Error", "error"); }
  };

  const startEditProgress = (p) => {
    setEditProgressId(p.id);
    setNewProgress({ hours: String(p.hours), description: p.description || "", work_date: p.work_date || "" });
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDep = (id) => setForm((f) => {
    const ids = f.dependency_ids.includes(id) ? f.dependency_ids.filter((x) => x !== id) : [...f.dependency_ids, id];
    return { ...f, dependency_ids: ids };
  });
  useEffect(() => {
    if (!depQuery || depQuery.length < 2) { setDepResults([]); return; }
    const q = depQuery.toLowerCase();
    const match = (t) => t.id !== task?.id && !form.dependency_ids.includes(t.id)
      && `${t.code || ""} ${t.title} ${t.project_name || ""}`.toLowerCase().includes(q);
    const local = allTasks.filter(match).slice(0, 8);
    setDepResults(local);
    if (form.project_id) {
      api.get(`/tasks?project_id=${form.project_id}`).then((r) => {
        const all = (r.data || []).filter(match);
        const merged = [...local];
        all.forEach((t) => { if (!merged.find((m) => m.id === t.id)) merged.push(t); });
        setDepResults(merged.slice(0, 10));
      }).catch(() => {});
    }
  }, [depQuery]);
  // Fases del proyecto seleccionado (jerarquía Fase 3.1).
  useEffect(() => {
    if (!form.project_id) { setPhaseOptions([]); return; }
    api.get(`/projects/${form.project_id}/phases`)
      .then((r) => setPhaseOptions(r.data || []))
      .catch(() => setPhaseOptions([]));
  }, [form.project_id]);
  const toggleAssignee = (id) => setForm((f) => {
    const ids = f.assignee_ids.includes(id) ? f.assignee_ids.filter((x) => x !== id) : [...f.assignee_ids, id];
    return { ...f, assignee_ids: ids };
  });
  const togglePmo = (id) => setForm((f) => {
    const ids = f.pmo_ids.includes(id) ? f.pmo_ids.filter((x) => x !== id) : [...f.pmo_ids, id];
    return { ...f, pmo_ids: ids };
  });

  const submit = async (e) => {
    e.preventDefault();
    const deptId = form.department_id || lockDept;
    const req = requirements[String(deptId)] || [];
    const labels = { due_date: "Fecha compromiso", start_date: "Fecha inicio", assignee_ids: "Responsable", task_type: "Tipo", description: "Descripcion" };
    for (const f of req) {
      const v = f === "assignee_ids" ? form.assignee_ids?.length : form[f];
      if (!v) return push(`Campo obligatorio: ${labels[f] || f}`, "error");
    }
    if (isProject && !form.project_id && !newProject.trim()) {
      return push("Una tarea de Proyecto debe pertenecer a un proyecto.", "error");
    }
    setBusy(true);
    try {
      const { dependency_ids: _deps, pending_files: _files, ...rest } = form;
      const payload = {
        ...rest,
        progress_pct: Number(rest.progress_pct) || 0,
        hours_spent: Number(rest.hours_spent) || 0,
        department_id: rest.department_id || null,
        project_id: newProject.trim() ? null : (rest.project_id || null),
        phase_id: (newProject.trim() || !rest.project_id) ? null : (rest.phase_id || null),
        new_project_name: newProject.trim() || null,
        start_date: rest.start_date || null,
        due_date: rest.due_date || null,
        start_at: isReunion ? (rest.start_at || null) : null,
        end_at: isReunion ? (rest.end_at || null) : null,
        tags: typeof rest.tags === "string" ? rest.tags.split(",").map((s) => s.trim()).filter(Boolean) : rest.tags,
        items: (rest.items || []).filter((i) => i.name && i.name.trim()).map((i) => {
          const { precio, ...resto } = i;
          return {
            ...resto,
            quantity: Number(i.quantity) || 0,
            // Se teclea en la moneda, se guarda en centavos (sin decimales que
            // se pierdan por el camino).
            precio_cents: precio === "" || precio === undefined
              ? (Number(i.precio_cents) || 0)
              : Math.round((Number(precio) || 0) * 100),
          };
        }),
      };
      let savedId;
      if (editing) {
        await api.patch(`/tasks/${task.id}`, payload);
        savedId = task.id;
      } else {
        const res = await api.post("/tasks", payload);
        savedId = res.data.id;
        // Avance inicial (pedido 2026-09-07): antes había que guardar la
        // tarea, volver a buscarla y editarla para recién ahí cargar horas.
        // Si se cargó algo en la pestaña "Avance inicial", se registra ahora
        // que ya existe el id — un solo paso para el usuario.
        if (supportsProgress && newProgress.description.trim()) {
          await api.post(`/tasks/${savedId}/progress`,
            { hours: 0, description: newProgress.description, work_date: newProgress.work_date || null })
            .catch((err) => push(err.response?.data?.detail || "No se pudo registrar el avance inicial", "error"));
        }
      }
      const existingDepIds = editing ? (task.dependencies?.map((d) => d.depends_on_id) || []) : [];
      const toAdd = form.dependency_ids.filter((id) => !existingDepIds.includes(id));
      const toRemove = editing ? (task.dependencies || []).filter((d) => !form.dependency_ids.includes(d.depends_on_id)) : [];
      await Promise.all([
        ...toAdd.map((depId) => api.post(`/tasks/${savedId}/dependencies`, { depends_on_id: depId }).catch(() => {})),
        ...toRemove.map((d) => api.delete(`/tasks/${savedId}/dependencies/${d.id}`).catch(() => {})),
      ]);
      // Adjuntos pendientes (seleccionados en el modal antes de guardar).
      // Se suben ahora que ya existe savedId; los fallos individuales no
      // rompen el guardado — se avisa al usuario con un toast.
      if (form.pending_files && form.pending_files.length > 0) {
        const results = await Promise.allSettled(
          form.pending_files.map((f) => {
            const fd = new FormData();
            fd.append("file", f);
            return api.post(`/tasks/${savedId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) push(`${failed} adjunto(s) fallaron al subir`, "warning");
      }
      push(editing ? "Tarea actualizada" : "Tarea creada", "success");
      if (newProject.trim()) onProjectCreated?.();
      onSaved?.();
      onClose();
    } catch (err) {
      push(err.response?.data?.detail || "Error al guardar", "error");
    } finally {
      setBusy(false);
    }
  };

  const assigneeOptions = (() => {
    const map = new Map(assignable.map((u) => [u.id, u]));
    (task?.assignees || []).forEach((a) => { if (!map.has(a.id)) map.set(a.id, a); });
    return [...map.values()].sort((a, b) => a.full_name.localeCompare(b.full_name));
  })();

  const filteredAssignees = assigneeFilter
    ? assigneeOptions.filter((u) => u.full_name.toLowerCase().includes(assigneeFilter.toLowerCase()))
    : assigneeOptions;

  const canDelete = editing && !readonly && (task?.creator_id === user?.id || user?.role === "admin");
  const removeTask = async () => {
    if (!window.confirm(`Vas a archivar la tarea "${task.title}".\n\nSe ocultará (no se borra de la base de datos; un administrador puede restaurarla).\n\n¿Continuar?`)) return;
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      push("Tarea archivada", "success");
      onSaved?.(); onClose();
    } catch (e) { push(e.response?.data?.detail || "No se pudo eliminar", "error"); }
    finally { setBusy(false); }
  };

  const deptReq = requirements[String(form.department_id || lockDept || "")] || [];
  const isReq = (field) => deptReq.includes(field);

  // ---- Tab: General ----
  const tabGeneral = (
    <div className="space-y-1">
      {/* Quién creó la tarea (solo al editar una existente) */}
      {editing && task?.creator && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
          <Icon name="user" size={12} className="text-slate-400" />
          Creado por <span className="font-semibold text-slate-600">{task.creator.full_name}</span>
          {task.created_at && <span>· {new Date(task.created_at).toLocaleDateString("es-VE")}</span>}
        </div>
      )}
      {/* 1) Dates FIRST — prominent cards */}
      {isReunion ? (
        <div className="rounded-lg border border-brand-100 bg-brand-50/30 p-2.5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <DateField label="Inicio reunión" icon="calendar" type="datetime-local" value={form.start_at} onChange={(e) => setStartDT(e.target.value)} />
            <DateField label="Fin reunión" icon="calendar" type="datetime-local" value={form.end_at} onChange={(e) => setEndDT(e.target.value)} />
            <div className="flex flex-col justify-center">
              <label className="lbl">% Avance</label>
              <input type="number" min="0" max="100" className="sel" value={form.progress_pct} onChange={(e) => set("progress_pct", e.target.value)} />
            </div>
            <div className="flex flex-col justify-center">
              <label className="lbl">Horas (auto)</label>
              <input type="number" className="sel" value={form.hours_spent} readOnly disabled />
            </div>
          </div>
          {form.start_at && form.end_at && (() => {
            const h = (new Date(form.end_at) - new Date(form.start_at)) / 3600000;
            return h > 0
              ? <p className="mt-1.5 text-[11px] font-medium text-emerald-600">Duración: {h.toFixed(2)} h</p>
              : <p className="mt-1.5 text-[11px] text-rose-500">La finalización debe ser posterior al inicio.</p>;
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <DateField label="Fecha inicio" icon="calendar" required={isReq("start_date")} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          <DateField label="Compromiso" icon="clock" required={isReq("due_date")} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
          <div className="rounded-lg border border-slate-200 p-2">
            <div className="mb-1 flex items-center gap-1.5">
              <Icon name="chart" size={13} className="text-slate-400" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">% Avance</span>
            </div>
            <input type="number" min="0" max="100" className="w-full border-0 bg-transparent p-0 text-[12px] font-medium text-slate-700 focus:outline-none focus:ring-0"
              value={form.progress_pct} onChange={(e) => set("progress_pct", e.target.value)} />
          </div>
          <div className="rounded-lg border border-slate-200 p-2">
            <div className="mb-1 flex items-center gap-1.5">
              <Icon name="clock" size={13} className="text-slate-400" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Tiempo invertido</span>
            </div>
            <HorasMin horas={form.hours_spent} onSave={(dec) => set("hours_spent", dec)} />
          </div>
        </div>
      )}

      <Divider label="clasificación" />

      {/* 2) Priority + Status + Type — chips */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="lbl">Prioridad</label>
          <div className="flex flex-wrap gap-1">
            {Object.entries(PRIORITY).map(([k, v]) => (
              <Chip key={k} selected={form.priority === k} onClick={() => set("priority", k)}
                color={form.priority === k ? v.color : ""}>
                <span className={`h-1.5 w-1.5 rounded-full ${form.priority === k ? "" : "bg-slate-300"}`} />{v.label}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <label className="lbl">Estado</label>
          <div className="flex flex-wrap gap-1">
            {Object.entries(STATUS).map(([k, v]) => (
              <Chip key={k} selected={form.status === k} onClick={() => set("status", k)}
                color={form.status === k ? v.color : ""}>
                <span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} />{v.label}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <label className="lbl">Tipo{isReq("task_type") && <span className="text-rose-500"> *</span>}</label>
          <div className="flex flex-wrap gap-1">
            {TASK_TYPES.map(([k, l]) => (
              <Chip key={k} selected={form.task_type === k} onClick={() => {
                if (k === "reunion" && form.start_date && !form.start_at) {
                  setForm((f) => ({ ...f, task_type: k, start_at: f.start_date + "T09:00", end_at: (f.due_date || f.start_date) + "T10:00" }));
                } else { set("task_type", k); }
              }}>{l}</Chip>
            ))}
          </div>
        </div>
      </div>

      <Divider label="organización" />

      {/* 3) Dept + Recurrence + Tags + Support */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div>
          <label className="lbl">Departamento</label>
          <select className="sel" value={form.department_id} onChange={(e) => set("department_id", e.target.value)} disabled={Boolean(lockDept)}>
            <option value="">— Sin depto —</option>
            <DeptOptions departments={deptOpts} />
          </select>
        </div>
        <div>
          <label className="lbl">Recurrencia</label>
          <select className="sel" value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)}>
            {RECURRENCE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="lbl">Etiquetas</label>
          <input className="sel" placeholder="coma-separadas" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
            onChange={(e) => set("tags", e.target.value)} />
        </div>
        <div>
          <label className="lbl">Apoyo requerido</label>
          <input className="sel" value={form.support_required} onChange={(e) => set("support_required", e.target.value)} />
        </div>
      </div>

      {/* Blocker */}
      {form.status === "blocked" && (
        <>
          <Divider />
          <div><label className="lbl">Motivo del bloqueo</label><input className="sel" placeholder="Alerta al supervisor" value={form.blocker_reason} onChange={(e) => set("blocker_reason", e.target.value)} /></div>
        </>
      )}

      <Divider label="dependencias y adjuntos" />

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
      {/* Dependencies */}
      <div>
        <div className="lbl mb-1">Dependencias</div>
        {form.dependency_ids.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1">
            {form.dependency_ids.map((id) => {
              const t = allTasks.find((x) => x.id === id)
                || (task?.dependencies?.find((d) => d.depends_on_id === id)?.depends_on)
                || depResults.find((x) => x.id === id);
              const label = t ? `${t.code ? t.code + " · " : ""}${t.title.length > 40 ? t.title.slice(0, 40) + "..." : t.title}` : `Tarea #${id}`;
              return (
                <span key={id} className="inline-flex items-center gap-1 rounded-tag border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium uppercase text-indigo-700">
                  <Icon name="link" size={11} />
                  {label}
                  <button type="button" onClick={() => toggleDep(id)} aria-label="Quitar dependencia" className="ml-0.5 text-indigo-400 hover:text-rose-500"><Icon name="x" size={11} /></button>
                </span>
              );
            })}
          </div>
        )}
        <div className="relative" ref={depAnchorRef}>
          <input className="sel" placeholder="Buscar tarea para agregar dependencia..." value={depQuery} onChange={(e) => setDepQuery(e.target.value)} />
          {depQuery && depResults.length > 0 && (
            <FloatingPopover anchorRef={depAnchorRef} maxHeight={160} className="rounded-lg border border-slate-200 bg-white shadow-lg">
              {depResults.map((t) => (
                <button key={t.id} type="button" onClick={() => { toggleDep(t.id); setDepQuery(""); }}
                  className="block w-full truncate px-3 py-1.5 text-left text-[11px] uppercase hover:bg-slate-50">
                  <Icon name="link" size={11} className="mr-1 text-slate-400" />
                  {t.code ? `${t.code} · ` : ""}{t.title}
                </button>
              ))}
            </FloatingPopover>
          )}
        </div>
        {form.dependency_ids.length === 0 && !depQuery && <p className="mt-1 text-[11px] text-slate-400">Sin dependencias. Busca una tarea para vincular.</p>}
      </div>

      <div>
        <div className="lbl mb-1">Adjuntos</div>
        {/* Adjuntos ya subidos (solo modo edición) — se listan aquí para no
            obligar al usuario a cerrar y abrir el panel de detalle. */}
        {editing && task?.attachments?.length > 0 && (
          <div className="mb-2 space-y-1">
            {task.attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px]">
                <Icon name="paperclip" size={12} className="text-slate-400" />
                <span className="flex-1 truncate" title={a.filename}>{a.filename}</span>
                <span className="text-[11px] text-slate-400">{Math.round((a.size || 0) / 1024)} KB</span>
              </div>
            ))}
          </div>
        )}
        {/* Archivos seleccionados pero aún NO subidos (se suben tras guardar). */}
        {form.pending_files.length > 0 && (
          <div className="mb-2 space-y-1">
            {form.pending_files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50/50 px-2 py-1 text-[11px]">
                <Icon name="paperclip" size={12} className="text-brand-500" />
                <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="text-[11px] text-slate-500">{Math.round(f.size / 1024)} KB</span>
                <button type="button" aria-label="Quitar"
                  onClick={() => setForm((prev) => ({ ...prev, pending_files: prev.pending_files.filter((_, idx) => idx !== i) }))}
                  className="text-slate-400 hover:text-rose-500"><Icon name="x" size={11} /></button>
              </div>
            ))}
            <p className="text-[11px] text-slate-400">Se subirán al guardar la tarea.</p>
          </div>
        )}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2.5 py-1.5 text-[11px] text-slate-600 hover:border-brand-400 hover:bg-brand-50/50 hover:text-brand-700">
          <Icon name="paperclip" size={12} />
          {form.pending_files.length > 0 || (editing && task?.attachments?.length > 0) ? "Agregar más adjuntos" : "Adjuntar archivos"}
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length === 0) return;
              setForm((prev) => ({ ...prev, pending_files: [...prev.pending_files, ...files] }));
              e.target.value = ""; // reset para permitir seleccionar el mismo archivo de nuevo
            }}
          />
        </label>
      </div>
      </div>
    </div>
  );

  // ---- Tab: Descripción ----
  const tabDescripcion = (
    <div className="space-y-3">
      <div>
        <label className="lbl">Descripción</label>
        <div className="flex gap-2">
          <textarea className="sel flex-1" rows={6} placeholder="Notas, detalles o @menciones..." value={form.description} onChange={(e) => set("description", e.target.value)} />
          <MicButton onText={(t) => set("description", (form.description ? form.description + " " : "") + t)} />
        </div>
        <p className="mt-1 text-[11px] text-slate-400">
          Los enlaces (https://…) se vuelven clicables automáticamente. También admite HTML básico:
          {" "}<code className="rounded bg-slate-100 px-1">&lt;img src="url"&gt;</code> para incrustar imágenes y
          {" "}<code className="rounded bg-slate-100 px-1">&lt;a href="url"&gt;texto&lt;/a&gt;</code> para enlaces con texto.
        </p>
      </div>
      <div>
        <label className="lbl">Memo de cierre / resultados</label>
        <div className="flex gap-2">
          <textarea className="sel flex-1" rows={4} placeholder="Al completar la tarea: resultados, conclusiones, entregables…" value={form.closure_note} onChange={(e) => set("closure_note", e.target.value)} />
          <MicButton onText={(t) => set("closure_note", (form.closure_note ? form.closure_note + " " : "") + t)} />
        </div>
      </div>
      {/* Campos personalizados de tarea (Fase 4.1) */}
      <CustomFieldsForm scope="task" departmentId={form.department_id || lockDept}
        taskType={form.task_type}
        value={form.custom_data} onChange={(cd) => set("custom_data", cd)} users={users} />
    </div>
  );

  // ---- Tab: Participantes ----
  // Participantes: el MISMO buscador que usa el proyecto (PersonPicker). Con
  // decenas de usuarios, una parrilla de fichas obliga a barrer con la vista;
  // aquí se escribe el nombre y se elige. Los ya elegidos quedan arriba y se
  // quitan de uno en uno.
  const tabParticipantes = (
    <div className="space-y-5">
      <div>
        <label className="lbl">Responsables</label>
        <p className="mb-1.5 text-[11px] text-slate-400">
          Quienes ejecutan la tarea. Aparecen en el tablero y reciben las notificaciones.
        </p>
        <PersonPicker users={assigneeOptions} multi values={form.assignee_ids || []}
          onChange={(ids) => setForm((f) => ({ ...f, assignee_ids: ids }))}
          placeholder="Buscar responsable por nombre o correo…" />
      </div>

      <div>
        <label className="lbl">Colaboradores</label>
        <p className="mb-1.5 text-[11px] text-slate-400">
          Acompañan sin ser responsables: ven la tarea, comentan y suben archivos.
        </p>
        <PersonPicker users={assigneeOptions} multi values={form.collaborator_ids || []}
          onChange={(ids) => setForm((f) => ({ ...f, collaborator_ids: ids }))}
          exclude={form.assignee_ids || []}
          accent="emerald"
          placeholder="Agregar colaboradores…" />
      </div>

      <div>
        <label className="lbl">PMO (oficina de proyectos)</label>
        <p className="mb-1.5 text-[11px] text-slate-400">
          Supervisa el avance. Si no hay usuarios con rol PMO, puedes elegir a cualquiera.
        </p>
        <PersonPicker
          users={assigneeOptions.some((u) => u.role === "pmo")
            ? assigneeOptions.filter((u) => u.role === "pmo") : assigneeOptions}
          multi values={form.pmo_ids || []}
          onChange={(ids) => setForm((f) => ({ ...f, pmo_ids: ids }))}
          accent="violet"
          placeholder="Buscar PMO…" />
      </div>
    </div>
  );


  // ---- Tab: Proyecto ----
  const tabProyecto = (
    <div className="space-y-3">
      <div>
        <label className="lbl">Proyecto existente</label>
        <select className="sel" value={newProject ? "" : (form.project_id || "")}
          disabled={Boolean(newProject)}
          onChange={(e) => set("project_id", e.target.value ? Number(e.target.value) : "")}>
          <option value="">— Sin proyecto —</option>
          {projects.map((p) => <option key={p.id} value={p.id} className="uppercase">{p.name}</option>)}
        </select>
      </div>
      {form.project_id && (
        <div>
          <label className="lbl">Fase / hito {phaseOptions.length === 0 && <span className="font-normal text-slate-400">(este proyecto no tiene fases)</span>}</label>
          <select className="sel" value={form.phase_id || ""}
            onChange={(e) => set("phase_id", e.target.value ? Number(e.target.value) : "")}>
            <option value="">— Sin fase —</option>
            {phaseOptions.map((p) => <option key={p.id} value={p.id}>{p.kind === "hito" ? "◆ " : ""}{p.name}</option>)}
          </select>
        </div>
      )}
      <Divider label="o crear nuevo" />
      <div>
        <label className="lbl">Nombre del nuevo proyecto</label>
        <input className="sel" placeholder="Escribe el nombre..." value={newProject}
          onChange={(e) => setNewProject(e.target.value)} />
      </div>
      {isProject && <p className="text-[11px] text-slate-400">Las tareas tipo Proyecto siempre deben pertenecer a un proyecto.</p>}
    </div>
  );

  // ---- Tab: Items ----
  const tabItems = (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-ghost text-[11px]" onClick={addItem}><Icon name="plus" size={12} /> Agregar</button>
        <button type="button" className="btn-ghost text-[11px]" onClick={() => setShowPaste((s) => !s)}><Icon name="sheet" size={12} /> Pegar Excel</button>
        <button type="button" className="btn-ghost text-[11px]" onClick={openTpls}><Icon name="copy" size={12} /> Importar plantilla</button>
        {items.length > 0 && <span className="text-[11px] text-slate-400">{items.length} item(s) · {items.filter((i) => i.done).length} listo(s) · cant.: {items.reduce((s, i) => s + (Number(i.quantity) || 0), 0)}
          {(() => {
            const t = items.reduce((s, i) => s + (Number(i.quantity) || 0) *
              (i.precio !== undefined && i.precio !== "" ? Number(i.precio) || 0 : (Number(i.precio_cents) || 0) / 100), 0);
            return t > 0 ? ` · estimado: ${t.toLocaleString("es-PA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
          })()}</span>}
      </div>
      {showPaste && (
        <div className="rounded-lg border border-dashed border-slate-300 p-2">
          <textarea className="sel font-mono" rows={3} placeholder={"Material[TAB]Cantidad"} value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
          <div className="mt-1 flex justify-end gap-1">
            <button type="button" className="btn-ghost text-[11px]" onClick={() => { setShowPaste(false); setPasteText(""); }}>Cancelar</button>
            <button type="button" className="btn-primary text-[11px]" onClick={parsePaste}>Importar</button>
          </div>
        </div>
      )}
      {tpls !== null && (
        <div className="rounded-lg border border-dashed border-slate-300 p-2">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-600">Elige una plantilla</span>
            <input className="sel h-6 flex-1 py-0 text-[11px]" placeholder="Buscar plantilla…"
              value={tplQ} onChange={(e) => setTplQ(e.target.value)} autoFocus />
            <button type="button" className="btn-ghost text-[11px]" onClick={() => setTpls(null)}>Cerrar</button>
          </div>
          {tpls.length === 0 ? (
            <p className="text-[11px] text-slate-400">No hay plantillas disponibles para este departamento.</p>
          ) : (
            <div className="max-h-52 space-y-1 overflow-y-auto">
              {tpls.filter((t) => !tplQ || `${t.name} ${usaMarca ? t.brand : ""} ${t.department || ""}`.toLowerCase().includes(tplQ.toLowerCase()))
                .map((t) => (
                <button type="button" key={t.id} onClick={() => importTpl(t)}
                  className="flex w-full items-center gap-2 rounded border border-slate-200 px-2 py-1.5 text-left hover:border-brand-300 hover:bg-brand-50/40">
                  <Icon name="copy" size={13} className="shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-medium text-slate-700">{t.name}{usaMarca && t.brand ? ` · ${t.brand}` : ""}</span>
                    <span className="block text-[11px] text-slate-400">{(t.items || []).length} ítem(s){t.department ? ` · ${t.department}` : " · global"}</span>
                  </span>
                  <Icon name="plus" size={13} className="shrink-0 text-brand-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-400">
              <tr><th className="w-8 px-1.5 py-1 text-center">OK</th><th className="px-1.5 py-1 text-left">Material</th><th className="w-16 px-1.5 py-1">Cant.</th><th className="w-14 px-1.5 py-1">U/M</th><th className="w-20 px-1.5 py-1">Precio</th><th className="w-28 px-1.5 py-1 text-left">Estado</th><th className="px-1.5 py-1 text-left">Nota</th><th className="w-6 px-1.5 py-1"></th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className={`border-t border-slate-100 ${it.done ? "bg-emerald-50/50" : ""}`}>
                  <td className="px-1 py-0.5 text-center"><input type="checkbox" className="h-3.5 w-3.5 accent-emerald-600" checked={Boolean(it.done)} onChange={(e) => setItem(i, "done", e.target.checked)} /></td>
                  <td className="px-1 py-0.5"><input className={`sel h-6 py-0 ${it.done ? "line-through" : ""}`} value={it.name || ""} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Material" /></td>
                  <td className="px-1 py-0.5"><input type="number" step="any" className="sel h-6 w-full py-0" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} /></td>
                  <td className="px-1 py-0.5"><input className="sel h-6 w-full py-0" value={it.unit || ""} onChange={(e) => setItem(i, "unit", e.target.value)} placeholder="u/m" /></td>
                  <td className="px-1 py-0.5"><input type="number" step="any" min="0" className="sel h-6 w-full py-0 text-right"
                    value={it.precio ?? (it.precio_cents ? (it.precio_cents / 100).toFixed(2) : "")}
                    onChange={(e) => setItem(i, "precio", e.target.value)} placeholder="0.00" /></td>
                  <td className="px-1 py-0.5">
                    <select className="sel h-6 w-full py-0" value={it.estado || ""} onChange={(e) => setItem(i, "estado", e.target.value)}>
                      <option value="">—</option>
                      {SUPPLY_ORDER.map((k) => <option key={k} value={k}>{SUPPLY[k].label}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-0.5"><input className="sel h-6 w-full py-0" value={it.nota || ""} onChange={(e) => setItem(i, "nota", e.target.value)} placeholder="nota" /></td>
                  <td className="px-1 py-0.5 text-center"><button type="button" onClick={() => removeItem(i)} aria-label="Quitar ítem" className="text-rose-400 hover:text-rose-600"><Icon name="x" size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const tabAvances = (
    <div className="space-y-3">

      <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50/40 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-700">
          {!editing ? "Avance inicial (opcional)" : editProgressId ? "Editando avance" : "Registrar avance"}
        </div>
        <textarea className="sel" rows={2} placeholder="¿Qué se avanzó? Ej: Levantamiento de información, revisión de documentos, reunión con proveedor…" value={newProgress.description} onChange={(e) => setNewProgress((p) => ({ ...p, description: e.target.value }))} />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Fecha:</label>
            <input type="date" className="sel w-36" value={newProgress.work_date} onChange={(e) => setNewProgress((p) => ({ ...p, work_date: e.target.value }))} />
          </div>
          <div className="flex-1" />
          {!editing ? (
            <span className="text-[11px] text-slate-400">Se guarda junto con la tarea al hacer clic en «Guardar».</span>
          ) : (
            <>
              {editProgressId && <button type="button" className="btn-ghost text-xs" onClick={() => { setEditProgressId(null); setNewProgress({ hours: "", description: "", work_date: todayLocal() }); }}>Cancelar</button>}
              <button type="button" className="btn-primary text-xs" disabled={progressBusy || !newProgress.description.trim()} onClick={saveProgress}>
                <Icon name={editProgressId ? "check" : "plus"} size={14} /> {editProgressId ? "Guardar" : "Agregar"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        {editing && progress.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">Aún no se registran avances</div>}
        {progress.map((p) => {
          const isOwn = p.author?.id === user?.id;
          return (
            <div key={p.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {p.author && <Avatar name={p.author.full_name} size={18} src={p.author.avatar_url ? `/api/users/${p.author.id}/avatar` : ""} />}
                  <span className="font-medium text-slate-700">{p.author?.full_name || "—"}</span>
                  <span>·</span>
                  {p.work_date && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">{p.work_date}</span>}
                  <span>{fmtStamp(p.created_at)}</span>
                </div>
                {isOwn && (
                  <div className="flex gap-1">
                    <button type="button" onClick={() => startEditProgress(p)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Editar"><Icon name="edit" size={13} /></button>
                    <button type="button" onClick={() => deleteProgressEntry(p.id)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Eliminar"><Icon name="trash" size={13} /></button>
                  </div>
                )}
              </div>
              {p.description && <div className="whitespace-pre-wrap text-sm text-slate-700">{p.description}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );

  const TABS = [
    { key: "general", label: "General", icon: "settings" },
    { key: "descripcion", label: "Descripción", icon: "fileText" },
    { key: "participantes", label: "Participantes", icon: "users", badge: form.assignee_ids.length },
    { key: "proyecto", label: "Proyecto", icon: "folder" },
  ];
  if (isInventory) TABS.push({ key: "items", label: "Ítems", icon: "sheet", badge: items.length });
  if (supportsProgress) TABS.push({ key: "avances", label: editing ? "Avances" : "Avance inicial", icon: "clock", badge: editing ? progress.length : undefined });

  const tabContent = { general: tabGeneral, descripcion: tabDescripcion, participantes: tabParticipantes, proyecto: tabProyecto, items: tabItems, avances: tabAvances };

  return (
    <Modal open={open} onClose={requestClose} title={null} xl>
      <form onSubmit={submit}>
        {/* Header with accent */}
        <div className="-mx-5 -mt-5 mb-4 rounded-t-xl border-b border-slate-200 bg-white px-5 pb-3 pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="tag-modulo bg-tareas/10 text-tareas">{editing ? "Editar tarea" : "Nueva tarea"}</span>
            <button type="button" onClick={requestClose} aria-label="Cerrar" className="shrink-0 rounded-full p-1 text-tareas/70 transition-colors hover:bg-tareas/10 hover:text-tareas">
              <Icon name="x" size={16} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input aria-label="Título de la tarea"
              className="w-full border-0 bg-transparent p-0 text-lg font-bold text-tinta placeholder:text-tinta-3 focus:outline-none focus:ring-0"
              required placeholder={editing ? "Título de la tarea" : "Escribe el título de la tarea…"} value={form.title} onChange={(e) => set("title", e.target.value)} />
            <MicButton onText={(t) => set("title", (form.title ? form.title + " " : "") + t)} />
          </div>
        </div>

        {/* Tab bar */}
        <TabTrack>
          {TABS.map((t) => (
            <TabButton key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} badge={t.badge}>
              <Icon name={t.icon} size={14} /> {t.label}
            </TabButton>
          ))}
        </TabTrack>

        {/* Tab content — fixed height prevents layout jump */}
        <div className="min-h-[340px]">
          {tabContent[tab]}
        </div>

        {/* Actions */}
        <div className="-mx-5 -mb-5 mt-4 flex items-center justify-between gap-2 rounded-b-xl border-t border-slate-100 bg-slate-50/50 px-5 py-3">
          {canDelete
            ? <button type="button" onClick={removeTask} disabled={busy} className="btn-ghost text-[11px] text-rose-600 hover:bg-rose-50"><Icon name="trash" size={13} /> Eliminar</button>
            : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={requestClose} className="btn-ghost text-[11px]">Cancelar</button>
            <button className="btn-primary text-[11px]" disabled={busy}>{busy ? "Guardando..." : "Guardar"}</button>
          </div>
        </div>
      </form>

      <style>{`
        .lbl { display: block; margin-bottom: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: rgb(148 163 184); }
        .sel { display: block; width: 100%; border-radius: 0.5rem; border: 1px solid rgb(226 232 240); padding: 6px 10px; font-size: 12px; color: rgb(51 65 85); background: white; transition: all 0.15s; }
        .sel:hover { border-color: rgb(203 213 225); }
        .sel:focus { outline: none; border-color: rgb(99 102 241); box-shadow: 0 0 0 2px rgb(99 102 241 / 0.15); }
        .sel:disabled { opacity: 0.5; cursor: not-allowed; }
        .sel::placeholder { color: rgb(203 213 225); }
        select.sel { padding-right: 28px; appearance: auto; }
        textarea.sel { resize: vertical; }
      `}</style>
    </Modal>
  );
}
